import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { FilterMatchMode } from 'primereact/api';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { InputNumber } from 'primereact/inputnumber';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Menu } from 'primereact/menu';
import { Skeleton } from 'primereact/skeleton';
import { errorService } from '../../services/ErrorService';
import { showErrorToast, showSuccessToast } from '../../utils/ToastUtils';
import { 
    selectLoading, 
    selectResidenceList,
    selectModuleList, 
    selectModuleListCount, 
    getModulesAsync, 
    getModulesCountAsync, 
    deleteModuleAsync, 
    editModuleAsync,
    createModuleAsync,
    setModuleResidenceAsync,
    initialLoadAsync
} from './moduleSlice';

const ModuleView = () => {

    let emptyModule = {
        id: null,
        description: "",
        weightPercentage: 0
    }

    const defaultGridParameters = {page: 0, first: 0, rows: 10, sortField: 'id', sortOrder: 1, filters: {}}
    
    const [deleteModuleDialog, setDeleteModuleDialog] = useState(false);
    
    const [moduleDialog, setModuleDialog] = useState(false);
    const [module, setModule] = useState(emptyModule);
    const [selectedModules, setSelectedModules] = useState(null);
    const [expandedRows, setExpandedRows] = useState(null);

    const [gridParameters, setGridParameters] = useState(defaultGridParameters);

    const toast = useRef(null);
    const dt = useRef(null);
    const menu = useRef(null);
    const [detail, setDetail] = useState({});

    const dispatch = useDispatch();
    const loading = useSelector(selectLoading);
    const residenceList = useSelector(selectResidenceList);
    const moduleList = useSelector(selectModuleList);
    const moduleListCount = useSelector(selectModuleListCount);

    useEffect(() => {
        const loadInitialData = async () => {
            try {
                await errorService.retryOperation(async () => {
                    await dispatch(initialLoadAsync(defaultGridParameters)).unwrap();
                });
            } catch (error) {
                showErrorToast(toast, 'Erro ao carregar módulos', error);
            }
        };

        loadInitialData();
    }, [dispatch]);
    
    const onSort = async (event) => {
        try {
            await errorService.retryOperation(async () => {
                const sortParams = {
                    ...gridParameters,
                    sortField: event.sortField,
                    sortOrder: event.sortOrder,
                    filters: {...gridParameters.filters}
                };
                setGridParameters(sortParams);
                await Promise.all([
                    dispatch(getModulesAsync(sortParams)).unwrap(),
                    dispatch(getModulesCountAsync(sortParams)).unwrap()
                ]);
            });
        } catch (error) {
            showErrorToast(toast, 'Erro ao ordenar módulos', error);
        }
    };

    const onFilter = async (event) => {
        try {
            await errorService.retryOperation(async () => {
                const filterParams = {
                    ...gridParameters,
                    page: 0,
                    first: 0,
                    filters: event.filters
                };
                setGridParameters(filterParams);
                await Promise.all([
                    dispatch(getModulesAsync(filterParams)).unwrap(),
                    dispatch(getModulesCountAsync(filterParams)).unwrap()
                ]);
            });
        } catch (error) {
            showErrorToast(toast, 'Erro ao filtrar módulos', error);
        }
    };
    
    const onPage = async (event) => {
        try {
            await errorService.retryOperation(async () => {
                const pageParams = {
                    ...event,
                    filters: {...gridParameters.filters}
                };
                setGridParameters(pageParams);
                await dispatch(getModulesAsync(pageParams)).unwrap();
            });
        } catch (error) {
            showErrorToast(toast, 'Erro ao mudar página', error);
        }
    };

    const openNew = () => {
        setModule(emptyModule);
        setModuleDialog(true);
    }

    const hideModuleDialog = () => {
        setModuleDialog(false);
    }

    const hideDeleteModuleDialog = () => {
        setDeleteModuleDialog(false);
    }

    const validateForm = () => {
        if (!module.description) {
            showErrorToast(toast, 'Erro de validação', 'O nome do módulo é obrigatório');
            return false;
        }

        if (module.weightPercentage === null || module.weightPercentage === undefined) {
            showErrorToast(toast, 'Erro de validação', 'O peso do módulo é obrigatório');
            return false;
        }

        return true;
    }

    const onSave = async () => {
        if (validateForm()) {
            try {
                await errorService.retryOperation(async () => {
                    if (module.id) {
                        await dispatch(editModuleAsync({module, gridParameters})).unwrap();
                        showSuccessToast(toast, 'Sucesso', 'Módulo atualizado com sucesso');
                    } else {
                        await dispatch(createModuleAsync({module, gridParameters})).unwrap();
                        showSuccessToast(toast, 'Sucesso', 'Módulo criado com sucesso');
                    }
                    hideModuleDialog();
                    await loadData();
                });
            } catch (error) {
                showErrorToast(toast, 'Erro ao salvar módulo', error);
            }
        }
    };

    const editModule = (module) => {
        setModule({ ...module });
        setModuleDialog(true);
    }

    const confirmDeleteModule = (module) => {
        setModule(module);
        setDeleteModuleDialog(true);
    }

    const deleteModule = async () => {
        try {
            await errorService.retryOperation(async () => {
                await dispatch(deleteModuleAsync({id: module.id, gridParameters})).unwrap();
                showSuccessToast(toast, 'Sucesso', 'Módulo excluído com sucesso');
                hideDeleteModuleDialog();
                setModule(emptyModule);
                await loadData();
            });
        } catch (error) {
            showErrorToast(toast, 'Erro ao excluir módulo', error);
        }
    };

    const exportCSV = () => {
        dt.current.exportCSV();
    }

    const confirmDeleteSelected = () => {
        // setDeleteModulesDialog(true);
    }

    const onInputChangeModule = (e, name) => {
        const val = (e.target && e.target.value) || '';
        let _module = { ...module };
        _module[`${name}`] = val;
        setModule(_module);
    }

    const onInputNumberChangeModule = (e, name) => {
        console.log('onInputNumberChangeModule', e)
        const val = e.value;
        let _module = { ...module };
        _module[`${name}`] = val;
        console.log('onInputChangeModule module', _module)
        setModule(_module);
    }

    const rowExpansionTemplate = (data) => {
        console.log('rowExpansionTemplate data', data)
        return (
            <div className="orders-subtable">
                <h5>Residências:</h5>
                <DataTable value={residenceList} responsiveLayout="scroll">
                    <Column field="id" header="ID" sortable style={{ width: '1em' }}></Column>
                    <Column field="description" header="Nome" sortable style={{ width: '1em' }}></Column>
                    <Column body={(rowData) => actionBodyTemplateResidences(rowData, data)} style={{ width: '1em', minWidth: '10rem' }}></Column> 
                </DataTable>
            </div>
        );
    }

    const actionBodyTemplateResidences = (rowData, data) => {
        console.log('actionBodyTemplateResidences rowData', rowData)
        console.log('actionBodyTemplateResidences data', data)
        return (
            <div className="actions">
                <Checkbox name="is_active" value={rowData.id} onChange={(e) => onChangeResidenceCheckbox(data.id, rowData.id)} checked={data.residences.filter(it => it.id == rowData.id).length > 0} />
            </div>
        );
    }

    const onChangeResidenceCheckbox = async (moduleId, residenceId) => {
        try {
            await errorService.retryOperation(async () => {
                await dispatch(setModuleResidenceAsync({moduleId, residenceId})).unwrap();
                showSuccessToast(toast, 'Sucesso', 'Residência atualizada com sucesso');
            });
        } catch (error) {
            showErrorToast(toast, 'Erro ao atualizar residência', error);
        }
    };

    const leftToolbarTemplate = () => {
        return (
            <React.Fragment>
                <div className="my-2">
                    <Button label="Novo módulo" icon="pi pi-plus" className="p-button-success mr-2" onClick={openNew} />
                    {/* <Button label="Delete" icon="pi pi-trash" className="p-button-danger" onClick={confirmDeleteSelected} disabled={!selectedProducts || !selectedProducts.length} /> */}
                </div>
            </React.Fragment>
        )
    }

    const toggleMenu = (event, data) => {
        setDetail(data)
        menu.current.toggle(event);
    };

    const toolbarItems = [
        {
            label: 'Editar',
            icon: 'pi pi-pencil',
            command: () => editModule(detail)
        },
        {
            label: 'Excluir',
            icon: 'pi pi-trash',
            command: () => confirmDeleteModule(detail)
        }
    ];

    const actionBodyTemplate = (rowData) => {
        return <>
            <Menu ref={menu} model={toolbarItems} popup />
            <Button type="button" label="Ações" icon="pi pi-angle-down" onClick={(event) => toggleMenu(event, rowData)} style={{ width: 'auto' }} />
        </>;
    }

    const idBodyTemplate = (rowData) => {
        return (
            <>
                <span className="p-column-title">ID</span>
                {rowData.id}
            </>
        );
    }

    const descriptionBodyTemplate = (rowData) => {
        return (
            <>
                <span className="p-column-title">UF</span>
                {rowData.description}
            </>
        );
    }

    const percentageBodyTemplate = (rowData) => {
        return (
            <>
                <span className="p-column-title">Peso(%)</span>
                {rowData.weightPercentage}
            </>
        );
    }

    const moduleDialogFooter = (
        <>
            <Button label="Cancelar" icon="pi pi-times" className="p-button-text" onClick={hideModuleDialog} />
            <Button label="Salvar" icon="pi pi-check" className="p-button-text" onClick={onSave} />
        </>
    );

    const deleteModuleDialogFooter = (
        <>
            <Button label="Não" icon="pi pi-times" className="p-button-text" onClick={hideDeleteModuleDialog} />
            <Button label="Sim" icon="pi pi-check" className="p-button-text" onClick={deleteModule} />
        </>
    );

    const loadData = async () => {
        try {
            await errorService.retryOperation(async () => {
                await Promise.all([
                    dispatch(getModulesAsync(gridParameters)).unwrap(),
                    dispatch(getModulesCountAsync(gridParameters)).unwrap()
                ]);
            });
        } catch (error) {
            showErrorToast(toast, 'Erro ao carregar dados', error);
        }
    };

    return (
      <>
        <div className="grid crud-demo">
            <div className="col-12">
                <div className="card">
                    <Toast ref={toast} />
                    <div className="flex justify-content-between align-items-center mb-3">
                        <h5 className="m-0">Gestão de módulos</h5>
                        <Button
                            icon="pi pi-refresh"
                            rounded
                            text
                            onClick={loadData}
                            disabled={loading}
                        />
                    </div>
                    <DataTable ref={dt} value={loading ? Array.from({ length: gridParameters.rows }, (_, i) => ({ id: `skeleton-${i}` })) : moduleList} selection={selectedModules} onSelectionChange={(e) => setSelectedModules(e.value)}
                        header={leftToolbarTemplate()}
                        dataKey="id"
                        paginator
                        sortField={gridParameters.sortField}
                        sortOrder={gridParameters.sortOrder}
                        rows={gridParameters.rows}
                        rowsPerPageOptions={[5, 10, 25, 50, 100]}
                        className="datatable-responsive"
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Exibindo de {first} à {last} de {totalRecords} registros"
                        emptyMessage="Sem resultados."
                        responsiveLayout="scroll"
                        onRowToggle={(e) => setExpandedRows(e.data)}
                        rowExpansionTemplate={rowExpansionTemplate}
                        expandedRows={expandedRows}
                        totalRecords={moduleListCount}
                        lazy={true}
                        first={gridParameters.first}
                        onSort={(e) => onSort(e)}
                        onFilter={(e) => onFilter(e)}
                        onPage={(e) => onPage(e)}
                        >
                        <Column expander style={{ width: '1em' }} body={loading ? () => <Skeleton /> : undefined} />
                        <Column field="id" header="ID" sortable={!loading} body={loading ? () => <Skeleton /> : idBodyTemplate} headerStyle={{ width: '1em', minWidth: '5rem' }}></Column>
                        <Column field="description" header="Nome" filterPlaceholder="Buscar por nome" showAddButton={false} showFilterOperator={false} filterMatchMode={FilterMatchMode.EQUALS} filterMatchModeOptions={[FilterMatchMode.EQUALS]} sortable={!loading} body={loading ? () => <Skeleton /> : descriptionBodyTemplate} headerStyle={{ width: '2em', minWidth: '10rem' }}></Column>
                        <Column field="weightPercentage" header="Peso(%)" sortable={!loading} body={loading ? () => <Skeleton /> : percentageBodyTemplate} headerStyle={{ width: '1em', minWidth: '10rem' }}></Column>
                        <Column body={loading ? () => <Skeleton /> : actionBodyTemplate} style={{ width: '1em', minWidth: '10rem' }}></Column>
                    </DataTable>

                    {/* Editar Módulo */}
                    <Dialog visible={moduleDialog} style={{ width: '450px' }} header="Editar módulo" modal className="p-fluid" footer={moduleDialogFooter} onHide={hideModuleDialog}>
                        <h5>{module.title}</h5>

                        <div className="field">
                            <label htmlFor="description">Nome</label>
                            <InputText id="description" value={module.description} onChange={(e) => onInputChangeModule(e, 'description')} required />
                        </div>

                        <div className="field">
                            <label htmlFor="weightPercentage">Peso(%)</label>
                            <InputNumber id="weightPercentage" min={0} mode="decimal" minFractionDigits={2} maxFractionDigits={20} value={module.weightPercentage} onChange={(e) => onInputNumberChangeModule(e, 'weightPercentage')} required />
                        </div>
                    </Dialog>
                    {/* FIM - Editar Módulo */}

                    <Dialog visible={deleteModuleDialog} style={{ width: '450px' }} header="Confirmar exclusão" modal footer={deleteModuleDialogFooter} onHide={hideDeleteModuleDialog}>
                        <div className="flex align-items-center justify-content-center">
                            <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                            {module && <span>Você deseja realmente excluir: <b>{module.description}</b>?</span>}
                        </div>
                    </Dialog>
                </div>
            </div>
        </div>
      </>
    );
}

const comparisonFn = function (prevProps, nextProps) {
    return prevProps.location.pathname === nextProps.location.pathname;
};

export default React.memo(ModuleView, comparisonFn);